import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CTextarea,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItem,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import Asset, { newAsset } from "../../models/asset";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import AssetTypeSelect from "../asset-type/AssetTypeSelect";
import AssetSubtypeSelect from "../asset-subtype/AssetSubtypeSelect";
import AssetType from "../../models/asset-type";
import AssetSubtype from "../../models/asset-subtype";
import AssetSelect from "./AssetSelect";
import Loading from "../indicators/Loading";

interface AssetFormProps {
  initialAsset?: Asset;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const AssetForm: React.FC<AssetFormProps> = ({
  initialAsset,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [loading, setLoading] = useState(true);
  const [editingAsset, setEditingAsset] = useState<Asset>(
    initialAsset ? initialAsset : newAsset()
  );
  const [selectedAssetType, setSelectedAssetType] = useState<
    AssetType | undefined
  >(undefined);
  const [selectedAssetSubtype, setSelectedAssetSubtype] = useState<
    AssetSubtype | undefined
  >(undefined);

  const [selectedParentAsset, setSelectedParentAsset] = useState<
    Asset | undefined
  >(undefined);

  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingAsset({
      ...editingAsset,
      name: e.target.value,
    });
  };

  const onCodeIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingAsset({
      ...editingAsset,
      codeIdentifier: e.target.value,
    });
  };

  const onNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingAsset({
      ...editingAsset,
      notes: e.target.value,
    });
  };

  const onIsActiveChange = (newIsActive: boolean) => {
    setEditingAsset({
      ...editingAsset,
      isActive: newIsActive,
    });
  };

  const onAssetTypeChange = (newAssetType: AssetType | null) => {
    setSelectedAssetType(newAssetType !== null ? newAssetType : undefined);
    setEditingAsset({
      ...editingAsset,
      typeId: newAssetType !== null ? newAssetType.id : undefined,
      typeName: newAssetType !== null ? newAssetType.name : undefined,
    });
  };

  const onAssetSubtypeChange = (newAssetSubtype: AssetType | null) => {
    setSelectedAssetSubtype(
      newAssetSubtype !== null ? newAssetSubtype : undefined
    );
    setEditingAsset({
      ...editingAsset,
      subtypeId: newAssetSubtype !== null ? newAssetSubtype.id : undefined,
      subtypeName: newAssetSubtype !== null ? newAssetSubtype.name : undefined,
    });
  };

  const onParentChange = (newParent: Asset | null) => {
    setSelectedParentAsset(newParent !== null ? newParent : undefined);
    setEditingAsset({
      ...editingAsset,
      parentAssetId: newParent !== null ? newParent.id : undefined,
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let toSendAsset = {
      ...editingAsset,
      companyId: company.id,
    };
    let requestPromise: Promise<ItemRequestStatus<Asset>>;
    if (editingAsset.id === undefined) {
      requestPromise = createItem<Asset>("/assets/create/", toSendAsset);
    } else {
      requestPromise = updateItem<Asset>(
        `/assets/${toSendAsset.id}/`,
        toSendAsset
      );
    }

    const assetStatus = await requestPromise;

    if (assetStatus.status !== SUCCESS) {
      if (assetStatus.errors !== undefined) {
        setErrors(assetStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (assetStatus.detail !== undefined) {
        message = assetStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingAsset(newAsset());
    setSelectedAssetSubtype(undefined);
    setSelectedAssetType(undefined);
    setSelectedParentAsset(undefined);
  };

  const fetchAssetType = async () => {
    if (initialAsset === undefined || initialAsset.typeId === undefined) {
      setSelectedAssetType(undefined);
      return;
    }
    const typeIdStr = initialAsset.typeId.toString();
    if (typeIdStr === undefined) {
      setSelectedAssetType(undefined);
      return;
    }
    const typeStatus = await getItem<AssetType>(`/asset_types/${typeIdStr}/`);
    if (typeStatus.status === SUCCESS) {
      if (typeStatus.data !== undefined) {
        setSelectedAssetType(typeStatus.data);
        return;
      }
    }
    setSelectedAssetType(undefined);
  };

  const fetchAssetSubtype = async () => {
    if (initialAsset === undefined || initialAsset.subtypeId === undefined) {
      setSelectedAssetSubtype(undefined);
      return;
    }
    const subtypeIdStr = initialAsset.subtypeId.toString();
    if (subtypeIdStr === undefined) {
      setSelectedAssetSubtype(undefined);
      return;
    }
    const subtypeStatus = await getItem<AssetSubtype>(
      `/asset_subtypes/${subtypeIdStr}/`
    );
    if (subtypeStatus.status === SUCCESS) {
      if (subtypeStatus.data !== undefined) {
        setSelectedAssetSubtype(subtypeStatus.data);
        return;
      }
    }
    setSelectedAssetSubtype(undefined);
  };

  const fetchAssetParent = async () => {
    if (
      initialAsset === undefined ||
      initialAsset.parentAssetId === undefined ||
      initialAsset.parentAssetId === null
    ) {
      setSelectedParentAsset(undefined);
      return;
    }
    const parentIdStr = initialAsset.parentAssetId.toString();
    if (parentIdStr === undefined) {
      setSelectedParentAsset(undefined);
      return;
    }
    const parentStatus = await getItem<Asset>(`/assets/${parentIdStr}/`);
    if (parentStatus.status === SUCCESS) {
      if (parentStatus.data !== undefined) {
        setSelectedParentAsset(parentStatus.data);
        return;
      }
    }
    setSelectedParentAsset(undefined);
  };

  const getInitialValues = async () => {
    setLoading(true);
    const parentPromise = fetchAssetParent();
    const typePromise = fetchAssetType();
    const subtypePromise = fetchAssetSubtype();
    await parentPromise;
    await typePromise;
    await subtypePromise;
    setLoading(false);
  };

  useEffect(() => {
    setEditingAsset(initialAsset ? initialAsset : newAsset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAsset]);

  useEffect(() => {
    getInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Código:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingAsset.codeIdentifier)}
                onChange={onCodeIdentifierChange}
                placeholder="Código"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("codeIdentifier", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingAsset.name)}
                onChange={onNameChange}
                placeholder="Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Tipo de Productor:</CLabel>
            </CCol>
            <CCol>
              <AssetTypeSelect
                value={selectedAssetType ? selectedAssetType : null}
                onChange={onAssetTypeChange}
              ></AssetTypeSelect>
              <FieldErrors
                errors={getFieldErrors("typeName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Subtipo de Productor:</CLabel>
            </CCol>
            <CCol>
              <AssetSubtypeSelect
                value={selectedAssetSubtype ? selectedAssetSubtype : null}
                onChange={onAssetSubtypeChange}
              ></AssetSubtypeSelect>
              <FieldErrors
                errors={getFieldErrors("subtypeName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Productor Padre:</CLabel>
            </CCol>
            <CCol>
              <AssetSelect
                value={selectedParentAsset ? selectedParentAsset : null}
                onChange={onParentChange}
                onlyParents={false}
              ></AssetSelect>
              <FieldErrors
                errors={getFieldErrors("parentAssetId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Notas:</CLabel>
            </CCol>
            <CCol>
              <CTextarea
                value={emptyValueOnUndefined(editingAsset.notes)}
                onChange={onNotesChange}
                placeholder="Notas..."
              ></CTextarea>
              <FieldErrors
                errors={getFieldErrors("notes", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Vigente:</CLabel>
            </CCol>
            <CCol>
              <CRow>
                <AnimatedCheckbox
                  value={editingAsset.isActive === true}
                  onChange={onIsActiveChange}
                ></AnimatedCheckbox>
              </CRow>

              <FieldErrors
                errors={getFieldErrors("isActive", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default AssetForm;
